import React from 'react';
import { ScrollArea } from "@/components/ui/scroll-area";

// Privacy Policy content component that can be used both in modal and as a page
export const PrivacyPolicyContent = () => {
  return (
    <div className="space-y-4 text-sm text-gray-600">

    <section className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-6">
      <h3 className="font-bold text-lg text-yellow-700">TRIAL VERSION NOTICE</h3>
      <p className="text-yellow-700">
        This Privacy Policy is provisional and specifically applies to the SurfSnap trial period. 
        During this trial, we are collecting user feedback and monitoring platform performance to 
        improve our services. A final version of this policy will be published upon full launch 
        of the platform.
      </p>
    </section>

    <section>
      <h3 className="font-medium text-lg">1. Introduction</h3>
      <p>
        Welcome to <strong>SurfSnap</strong> ("we," "us," or "our"). This Privacy
        Policy explains how we collect, use, disclose, and safeguard your information
        when you use our website, mobile application, and related services
        (collectively, the "Platform"). By accessing or using the Platform, you
        agree to the terms of this Privacy Policy.
      </p>
    </section>

    <section>
      <h3 className="font-medium text-lg">2. Information We Collect</h3>
      <p>
        <strong>2.1 Personal Data.</strong> We may collect personally identifiable
        information (such as your name, email address, or payment details) when you
        sign up, make a purchase, or otherwise interact with the Platform.
      </p>
      <p>
        <strong>2.2 Non-Personal Data.</strong> We may also collect non-personal
        information, such as device type, browser type, location (if enabled),
        or aggregated usage data for statistical purposes.
      </p>
      <p>
        <strong>2.3 Cookies &amp; Similar Technologies.</strong> We may use cookies,
        web beacons, and similar tracking technologies to enhance your experience,
        show personalized content, and gather usage analytics. You can configure
        your browser to reject cookies or notify you when a cookie is being used,
        but some features of the Platform may not function properly if you do so.
      </p>
      <p>
        <strong>2.4 Special Categories of Data.</strong> Our Platform may collect and process 
        photos that could potentially contain images of identifiable individuals. These photos 
        are only processed with the appropriate consent from photographers, and we implement 
        safeguards to protect the privacy of individuals who may appear in these photos.
      </p>
    </section>

    <section>
      <h3 className="font-medium text-lg">3. How We Use Your Information</h3>
      <p>
        We use the information we collect to:
      </p>
      <ul className="list-disc pl-4">
        <li>Provide, maintain, and improve the Platform's functionality.</li>
        <li>Process your transactions and send updates about your purchases.</li>
        <li>Respond to your inquiries and offer customer support.</li>
        <li>Show you ads or recommendations that may be relevant to surfers and photographers.</li>
        <li>Analyze usage patterns to improve our services.</li>
        <li>Comply with legal obligations and enforce our policies.</li>
      </ul>
    </section>

    <section>
      <h3 className="font-medium text-lg">4. Data Storage and Processing</h3>
      <p>
        Your data is stored and processed on secure servers located in the United States. 
        For users accessing our Platform from other regions, please note that your information 
        may be transferred to, stored, and processed in a country different from your residence. 
        By using the Platform, you consent to this transfer.
      </p>
      <p>
        We use industry-standard security measures to protect your data, including encryption, 
        access controls, and regular security assessments. All payment information is processed 
        through our secure payment processors, Stripe and PayPal, and we do not store complete 
        payment card details on our servers.
      </p>
    </section>

    <section>
      <h3 className="font-medium text-lg">5. Interest-Based Advertising</h3>
      <p>
        We may partner with third-party advertising networks or social media
        platforms to display ads relevant to surfers or photographers. These
        partners use cookies or other tracking technologies to gather information
        about your browsing activities on our Platform and other websites,
        in order to serve you more relevant ads. You can opt out of
        interest-based advertising through mechanisms provided by these partners,
        or by adjusting your device and browser settings.
      </p>
    </section>

    <section>
      <h3 className="font-medium text-lg">6. Third-Party Services & Analytics</h3>
      <p>
        We may use third-party service providers (e.g., analytics platforms like
        Google Analytics) to monitor and analyze the use of our Platform. These
        third parties may have access to your personal data only to perform tasks
        on our behalf and are obligated not to disclose or use it for any other
        purpose.
      </p>
      <p>
        For payment processing, we use industry-leading services:
      </p>
      <ul className="list-disc pl-4">
        <li><strong>Stripe:</strong> For credit/debit card payments. View their <a href="https://stripe.com/privacy" className="text-blue-600">Privacy Policy</a>.</li>
        <li><strong>PayPal:</strong> For PayPal-based transactions. View their <a href="https://www.paypal.com/us/webapps/mpp/ua/privacy-full" className="text-blue-600">Privacy Policy</a>.</li>
      </ul>
    </section>

    <section>
      <h3 className="font-medium text-lg">7. Data Sharing & Disclosure</h3>
      <p>
        We do not sell or rent your personal information. However, we may share
        information in the following circumstances:
      </p>
      <ul className="list-disc pl-4">
        <li>
          <strong>With Service Providers:</strong> To help us operate and maintain
          our Platform (e.g., payment processors).
        </li>
        <li>
          <strong>For Legal Reasons:</strong> If required by law or to protect
          our rights, property, or safety, or that of others.
        </li>
        <li>
          <strong>Business Transfers:</strong> In connection with a merger,
          acquisition, or sale of assets.
        </li>
      </ul>
    </section>

    <section>
      <h3 className="font-medium text-lg">8. Data Security, Retention, and Breach Notification</h3>
      <p>
        <strong>8.1 Security Measures.</strong> We take reasonable measures to protect your information from unauthorized
        access, alteration, or destruction. However, no security system is
        foolproof.
      </p>
      <p>
        <strong>8.2 Data Retention.</strong> We retain your personal data only for as long as necessary
        to fulfill the purposes outlined in this policy or comply with legal
        requirements. For user accounts, we maintain your data as long as your account
        is active, plus a retention period thereafter for legal compliance purposes.
      </p>
      <p>
        <strong>8.3 Data Breach Notification.</strong> In the event of a data breach that compromises
        your personal information, we will notify affected users without undue delay,
        typically within 72 hours of becoming aware of the breach, in accordance with
        applicable law. This notification will include details about the breach and
        steps we are taking to address it.
      </p>
    </section>

    <section>
      <h3 className="font-medium text-lg">9. Refund Policy</h3>
      <p>
        All purchases made through SurfSnap are final. We do not offer refunds for any transaction.
        By using our Platform, you acknowledge and agree to this no-refund policy.
      </p>
    </section>

    <section>
      <h3 className="font-medium text-lg">10. Account Deletion and Your Data Rights</h3>
      <p>
        <strong>10.1 Account Deletion.</strong> You may request deletion of your account at any time
        by contacting us at support@surfsnap.com. When you delete your account, your personal profile
        information will be removed from our active databases. However, some information may be retained
        for legal or legitimate business purposes, such as transaction records for financial reporting.
      </p>
      <p>
        <strong>10.2 Your Data Rights.</strong> Depending on your location, you may have certain rights
        regarding your personal data:
      </p>
      <ul className="list-disc pl-4">
        <li>The right to access your personal data</li>
        <li>The right to rectify inaccurate information</li>
        <li>The right to request deletion of your data</li>
        <li>The right to restrict or object to processing</li>
        <li>The right to data portability</li>
        <li>The right to withdraw consent at any time</li>
      </ul>
      <p>
        To exercise these rights, please contact us at <a href="mailto:privacy@surfsnap.com" className="text-blue-600">privacy@surfsnap.com</a>.
      </p>
    </section>

    <section>
      <h3 className="font-medium text-lg">11. Children's Privacy</h3>
      <p>
        Our Platform is not intended for children under the age of 13 (or
        the equivalent minimum age in the relevant jurisdiction). We do not
        knowingly collect personal information from children without parental
        consent. If you become aware that your child has provided us with
        personal data without your consent, please contact us so we can take
        appropriate action.
      </p>
    </section>

    <section>
      <h3 className="font-medium text-lg">12. Privacy for California Residents (CCPA)</h3>
      <p>
        If you are a California resident, the California Consumer Privacy Act (CCPA) 
        provides you with specific rights regarding your personal information. Under 
        the CCPA, you have the right to request disclosure of the personal information 
        we collect, use, disclose, and sell. You also have the right to request deletion 
        of your personal information and to not be discriminated against for exercising 
        your privacy rights.
      </p>
      <p>
        To exercise your CCPA rights, please contact us using the contact information 
        provided at the end of this Privacy Policy.
      </p>
    </section>

    <section>
      <h3 className="font-medium text-lg">13. Privacy for European Residents (GDPR)</h3>
      <p>
        If you are a resident of the European Economic Area (EEA), you have certain 
        data protection rights under the General Data Protection Regulation (GDPR). 
        Our legal basis for collecting and using your personal information depends 
        on the specific information concerned and the context in which we collect it.
      </p>
      <p>
        We may process your personal data because:
      </p>
      <ul className="list-disc pl-4">
        <li>We need to perform a contract with you</li>
        <li>You have given us consent to do so</li>
        <li>The processing is in our legitimate interests and not overridden by your rights</li>
        <li>We need to comply with legal obligations</li>
      </ul>
      <p>
        If you have questions about the legal basis for processing, please contact us.
      </p>
    </section>

    <section>
      <h3 className="font-medium text-lg">14. International Data Transfers</h3>
      <p>
        If you are accessing our Platform from outside the country in which we
        operate, please be aware that your information may be transferred to and
        processed in countries that may have data protection laws different from
        your jurisdiction. We ensure appropriate safeguards are in place to protect
        your information when transferred internationally, including using standard 
        contractual clauses where applicable.
      </p>
    </section>

    <section>
      <h3 className="font-medium text-lg">15. Changes to This Privacy Policy</h3>
      <p>
        We may update this Privacy Policy from time to time. Any changes will be
        posted on this page with an updated revision date. If we make significant
        changes, we may notify you via email or through the Platform.
      </p>
    </section>

    <section>
      <h3 className="font-medium text-lg">16. Contact Us</h3>
      <p>
        If you have questions or concerns about this Privacy Policy or our
        data practices, please contact us at 
        <a href="mailto:privacy@surfsnap.com" className="text-blue-600">
          {" "}privacy@surfsnap.com
        </a>.
      </p>
    </section>

  </div>
);
};